import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Loader } from "@/components/Loader";
import { ChevronLeft, Upload, Plus, Trash2, CheckCircle2 } from "lucide-react";
import PdfViewer from "@/components/nurse/PdfViewer";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

function emptyQuestion(type = "mcq") {
  return type === "mcq"
    ? { type: "mcq", text: "", options: ["", "", "", ""], correct_index: 0, explanation: "", points: 1 }
    : { type: "short", text: "", options: [], correct_index: null, explanation: "", points: 1 };
}

export default function ModuleEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const fileRef = useRef();
  const [module, setModule] = useState(null);
  const [form, setForm] = useState(null);
  const [sets, setSets] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [title, setTitle] = useState("Module Assessment");

  const load = async () => {
    try {
      const [m, a] = await Promise.all([api.get("/admin/modules"), api.get(`/admin/modules/${id}/assessments`)]);
      const mod = m.data.modules.find((x) => x.id === id);
      setModule(mod);
      setForm({ title: mod.title, description: mod.description || "", youtube_url: mod.youtube_url || "", estimated_minutes: mod.estimated_minutes, order: mod.order, is_active: mod.is_active });
      setSets(a.data.sets);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, [id]);

  const saveModule = async () => {
    try { await api.put(`/admin/modules/${id}`, { ...form, estimated_minutes: Number(form.estimated_minutes), order: Number(form.order) }); toast.success("Module saved"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const deleteModule = async () => {
    try { await api.delete(`/admin/modules/${id}`); toast.success("Module deleted"); navigate("/admin/modules"); }
    catch (e) { toast.error(apiError(e)); }
  };

  const uploadPdf = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/modules/${id}/upload-pdf`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PDF uploaded");
      load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const addQ = (type) => setQuestions([...questions, emptyQuestion(type)]);
  const updateQ = (i, patch) => setQuestions(questions.map((q, idx) => (idx === i ? { ...q, ...patch } : q)));
  const removeQ = (i) => setQuestions(questions.filter((_, idx) => idx !== i));
  const updateOpt = (qi, oi, val) => updateQ(qi, { options: questions[qi].options.map((o, idx) => (idx === oi ? val : o)) });

  const saveAssessment = async (publish) => {
    if (publish && questions.length !== 10) return toast.error("Publishing requires exactly 10 questions");
    if (questions.length === 0) return toast.error("Add questions first");
    try {
      await api.post(`/admin/modules/${id}/assessments`, { title, questions, publish });
      toast.success(publish ? "Assessment published" : "Assessment saved as draft");
      setQuestions([]);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const publishExisting = async (setId) => {
    try { await api.post(`/admin/assessments/${setId}/publish`); toast.success("Published"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!module || !form) return <Loader />;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="module-editor">
      <button onClick={() => navigate("/admin/modules")} className="flex items-center gap-1 text-sm font-semibold text-[#003d9b]">
        <ChevronLeft className="w-4 h-4" /> Modules
      </button>
      <h1 className="text-[32px] font-bold text-[#1c1b1b]">{module.title}</h1>

      {/* Module details */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6 space-y-3">
        <p className="font-semibold text-[#1c1b1b]">Module Details</p>
        <input data-testid="edit-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
          className="h-11 w-full rounded-lg border border-[#E5E7EB] px-3 focus:outline-none focus:ring-2 focus:ring-[#003d9b]" placeholder="Title" />
        <textarea data-testid="edit-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
          className="min-h-[70px] w-full rounded-lg border border-[#E5E7EB] p-3 focus:outline-none focus:ring-2 focus:ring-[#003d9b]" placeholder="Description" />
        <input data-testid="edit-youtube-input" value={form.youtube_url} onChange={(e) => setForm({ ...form, youtube_url: e.target.value })}
          className="h-11 w-full rounded-lg border border-[#E5E7EB] px-3 focus:outline-none focus:ring-2 focus:ring-[#003d9b]" placeholder="YouTube URL" />
        <div className="flex gap-3">
          <input data-testid="edit-minutes-input" type="number" value={form.estimated_minutes} onChange={(e) => setForm({ ...form, estimated_minutes: e.target.value })}
            className="h-11 w-full rounded-lg border border-[#E5E7EB] px-3 focus:outline-none focus:ring-2 focus:ring-[#003d9b]" placeholder="Minutes" />
          <input data-testid="edit-order-input" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })}
            className="h-11 w-full rounded-lg border border-[#E5E7EB] px-3 focus:outline-none focus:ring-2 focus:ring-[#003d9b]" placeholder="Order" />
          <label className="flex items-center gap-2 text-sm whitespace-nowrap px-3">
            <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4 accent-[#003d9b]" /> Active
          </label>
        </div>
        <div className="flex items-center gap-3">
          <button data-testid="save-module-btn" onClick={saveModule} className="h-11 px-5 rounded-lg bg-[#003d9b] text-white font-semibold hover:bg-[#0052cc] transition-colors">Save Details</button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button data-testid="delete-module-btn" className="h-11 px-5 rounded-lg border border-[#ba1a1a]/30 text-[#ba1a1a] font-semibold flex items-center gap-2 hover:bg-red-50 transition-colors">
                <Trash2 className="w-4 h-4" /> Delete Module
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent data-testid="delete-module-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete this module?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes the module, its slides, assessment sets, and all nurse progress and results for it. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
                <AlertDialogAction data-testid="confirm-delete-btn" onClick={deleteModule} className="bg-[#ba1a1a] hover:bg-[#a01717]">Delete</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Presentation PDF */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
        <p className="font-semibold text-[#1c1b1b] mb-2">Presentation (PDF)</p>
        <p className="text-sm text-[#434654] mb-4">
          {module.pdf_path ? `Uploaded: ${module.pdf_filename}` : "No presentation uploaded. Upload a PDF — it displays to nurses exactly as authored."}
        </p>
        <input ref={fileRef} type="file" accept=".pdf,application/pdf" hidden onChange={uploadPdf} data-testid="pdf-file-input" />
        <button data-testid="upload-pdf-btn" disabled={uploading} onClick={() => fileRef.current?.click()}
          className="h-11 px-5 rounded-lg border border-[#003d9b] text-[#003d9b] font-semibold flex items-center gap-2 hover:bg-[#003d9b]/5 transition-colors disabled:opacity-50">
          <Upload className="w-4 h-4" /> {uploading ? "Uploading…" : (module.pdf_path ? "Replace PDF" : "Upload PDF")}
        </button>
        {module.pdf_path && (
          <div className="mt-4 max-w-md" data-testid="pdf-preview">
            <PdfViewer pdfUrl={fileUrl(module.pdf_path)} compact />
          </div>
        )}
      </div>

      {/* Existing assessment sets */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
        <p className="font-semibold text-[#1c1b1b] mb-3">Assessment Sets</p>
        {sets.length === 0 ? <p className="text-sm text-[#434654]">No assessment sets yet.</p> : (
          <div className="space-y-2">
            {sets.map((s) => (
              <div key={s.id} data-testid={`assessment-set-${s.id}`} className="flex items-center justify-between border border-[#E5E7EB] rounded-lg px-4 py-3">
                <div>
                  <p className="font-medium text-[#1c1b1b]">{s.title}</p>
                  <p className="text-xs text-[#434654]">{s.questions.length} questions</p>
                </div>
                {s.published ? (
                  <span className="text-xs font-bold text-[#004e33] flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> Published</span>
                ) : (
                  <button data-testid={`publish-set-${s.id}`} onClick={() => publishExisting(s.id)}
                    className="text-sm font-semibold text-[#003d9b] hover:underline">Publish</button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Assessment builder */}
      <div className="bg-white border border-[#E5E7EB] rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <p className="font-semibold text-[#1c1b1b]">Build New Assessment Set</p>
          <span className={`text-sm font-semibold ${questions.length === 10 ? "text-[#004e33]" : "text-[#434654]"}`} data-testid="question-count-indicator">
            {questions.length}/10 questions
          </span>
        </div>
        <input data-testid="assessment-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Set title"
          className="h-11 w-full rounded-lg border border-[#E5E7EB] px-3 mb-4 focus:outline-none focus:ring-2 focus:ring-[#003d9b]" />

        <div className="space-y-4">
          {questions.map((q, i) => (
            <div key={i} data-testid={`builder-question-${i}`} className="border border-[#E5E7EB] rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold uppercase tracking-wider text-[#434654]">Q{i + 1} · {q.type === "mcq" ? "Multiple Choice" : "Short Answer"}</span>
                <button data-testid={`remove-question-${i}`} onClick={() => removeQ(i)} className="text-[#ba1a1a]"><Trash2 className="w-4 h-4" /></button>
              </div>
              <input data-testid={`question-text-${i}`} value={q.text} onChange={(e) => updateQ(i, { text: e.target.value })} placeholder="Question text"
                className="h-11 w-full rounded-lg border border-[#E5E7EB] px-3 mb-2 focus:outline-none focus:ring-2 focus:ring-[#003d9b]" />
              {q.type === "mcq" && q.options.map((opt, oi) => (
                <div key={oi} className="flex items-center gap-2 mb-2">
                  <input type="radio" name={`correct-${i}`} checked={q.correct_index === oi} onChange={() => updateQ(i, { correct_index: oi })} className="accent-[#003d9b]" data-testid={`correct-${i}-${oi}`} />
                  <input data-testid={`option-${i}-${oi}`} value={opt} onChange={(e) => updateOpt(i, oi, e.target.value)} placeholder={`Option ${oi + 1}`}
                    className="h-10 flex-1 rounded-lg border border-[#E5E7EB] px-3 focus:outline-none focus:ring-2 focus:ring-[#003d9b]" />
                </div>
              ))}
              <input data-testid={`explanation-${i}`} value={q.explanation} onChange={(e) => updateQ(i, { explanation: e.target.value })} placeholder="Explanation (shown in review)"
                className="h-10 w-full rounded-lg border border-[#E5E7EB] px-3 mt-1 focus:outline-none focus:ring-2 focus:ring-[#003d9b]" />
            </div>
          ))}
        </div>

        <div className="flex gap-2 mt-4">
          <button data-testid="add-mcq-btn" onClick={() => addQ("mcq")} className="h-10 px-4 rounded-lg border border-[#E5E7EB] font-semibold text-sm flex items-center gap-1 hover:bg-gray-50 transition-colors"><Plus className="w-4 h-4" /> MCQ</button>
          <button data-testid="add-short-btn" onClick={() => addQ("short")} className="h-10 px-4 rounded-lg border border-[#E5E7EB] font-semibold text-sm flex items-center gap-1 hover:bg-gray-50 transition-colors"><Plus className="w-4 h-4" /> Short Answer</button>
        </div>
        <div className="flex gap-3 mt-4">
          <button data-testid="save-draft-btn" onClick={() => saveAssessment(false)} className="h-11 px-5 rounded-lg border border-[#E5E7EB] font-semibold hover:bg-gray-50 transition-colors">Save Draft</button>
          <button data-testid="publish-assessment-btn" onClick={() => saveAssessment(true)}
            className="h-11 px-5 rounded-lg bg-[#003d9b] text-white font-semibold hover:bg-[#0052cc] transition-colors disabled:opacity-50" disabled={questions.length !== 10}>
            Publish (requires 10)
          </button>
        </div>
      </div>
    </div>
  );
}
